/**
 * Workflow NodePalette (P2.1-S5 polish).
 *
 * Trước: 3 group luôn hiển thị, list dọc.
 * Sau: group toggle expand/collapse + grid 2-col card style nhất quán với
 * Form Palette.
 */
import {
  Bot,
  Box,
  ChevronDown,
  Clock,
  Flag,
  GitBranch,
  Hand,
  Layers,
  type LucideIcon,
  Mail,
  Play,
  Square,
  Wrench,
} from "lucide-react";
import * as React from "react";

import type { NodeType } from "@/api/workflows";
import { cn } from "@/lib/utils";
import {
  NODE_TYPE_LABELS,
  useWorkflowDesignerStore,
} from "@/stores/workflowDesigner";

type GroupKey = "control" | "exec" | "ui";

interface NodePaletteItem {
  type: NodeType;
  Icon: LucideIcon;
  group: GroupKey;
}

const ITEMS: NodePaletteItem[] = [
  { type: "trigger", Icon: Play, group: "control" },
  { type: "branch", Icon: GitBranch, group: "control" },
  { type: "parallel", Icon: Layers, group: "control" },
  { type: "delay", Icon: Clock, group: "control" },
  { type: "end", Icon: Square, group: "control" },
  { type: "llm", Icon: Bot, group: "exec" },
  { type: "tool", Icon: Wrench, group: "exec" },
  { type: "subflow", Icon: Box, group: "exec" },
  { type: "form", Icon: Flag, group: "ui" },
  { type: "human", Icon: Hand, group: "ui" },
  { type: "notify", Icon: Mail, group: "ui" },
];

const GROUP_LABELS: Record<GroupKey, string> = {
  control: "Control flow",
  exec: "Execute",
  ui: "User interaction",
};

export function NodePalette(): JSX.Element {
  const addNode = useWorkflowDesignerStore((s) => s.addNode);
  const groups: GroupKey[] = ["control", "exec", "ui"];

  // All groups default open. Persist trong session memory thôi.
  const [openMap, setOpenMap] = React.useState<Record<GroupKey, boolean>>({
    control: true,
    exec: true,
    ui: true,
  });

  const toggle = (group: GroupKey): void =>
    setOpenMap((m) => ({ ...m, [group]: !m[group] }));

  return (
    <aside className="w-56 shrink-0 border-r bg-muted/20 overflow-y-auto">
      <div className="px-3 py-2 border-b bg-background sticky top-0 z-10">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase">
          Node Palette
        </h2>
      </div>
      <div className="p-2 space-y-2">
        {groups.map((group) => {
          const open = openMap[group];
          const items = ITEMS.filter((it) => it.group === group);
          return (
            <div key={group}>
              <button
                type="button"
                onClick={() => toggle(group)}
                className="w-full flex items-center gap-1.5 px-1 py-1 text-[10px] uppercase font-semibold text-muted-foreground hover:text-foreground transition-colors"
                aria-expanded={open}
              >
                <ChevronDown
                  className={cn(
                    "size-3 transition-transform",
                    !open && "-rotate-90",
                  )}
                />
                <span>{GROUP_LABELS[group]}</span>
                <span className="ml-auto opacity-60">{items.length}</span>
              </button>
              {open ? (
                <div className="grid grid-cols-2 gap-1.5 px-0.5 pt-0.5">
                  {items.map((it) => (
                    <NodeCard
                      key={it.type}
                      Icon={it.Icon}
                      label={NODE_TYPE_LABELS[it.type]}
                      onClick={() => addNode(it.type)}
                      onDragStart={(e) => {
                        e.dataTransfer.setData("application/x-workflow-node", it.type);
                        e.dataTransfer.effectAllowed = "copy";
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

interface NodeCardProps {
  Icon: LucideIcon;
  label: string;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
}

function NodeCard({ Icon, label, onClick, onDragStart }: NodeCardProps): JSX.Element {
  return (
    <button
      type="button"
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      title={`Click hoặc kéo ${label}`}
      className={cn(
        "flex flex-col items-center justify-center gap-1.5 rounded-md border bg-background py-2.5 px-1",
        "text-[11px] leading-tight text-center",
        "hover:border-primary/50 hover:bg-gradient-brand-soft hover:shadow-sm",
        "active:scale-[0.97] transition-all cursor-grab active:cursor-grabbing",
      )}
    >
      <Icon className="size-5 text-muted-foreground" />
      <span className="line-clamp-1">{label}</span>
    </button>
  );
}
